import {
  PAYMENT_METHOD_KINDS,
  PAYMENT_METHOD_KIND_LABELS,
  type PaymentMethodKind,
  type PaymentMethodRecord,
} from '@clim/domain';
import { useEffect, useState } from 'react';
import { ErrorBox, Field, Input, Modal, Select, Textarea } from '../../components/ui';
import { useApi, useMutation } from './common';

interface StripeStatus {
  /** « direct » : entreprise en Stripe Direct (configuration interne du serveur, aucun compte à relier) */
  mode: 'connect' | 'direct';
  serverReady: boolean;
  connected: boolean;
  chargesEnabled: boolean;
  detailsSubmitted: boolean;
}

/**
 * Paiement par carte : l'entreprise relie SON compte Stripe (inscription faite chez Stripe, aucune clé à copier).
 * L'argent des clients va directement sur ce compte ; la plateforme ne prend aucune commission.
 */
function StripeConnectCard({ onChanged }: { onChanged(): void }) {
  const status = useApi<StripeStatus>('/v1/admin/stripe');
  const m = useMutation();
  const back = new URLSearchParams(window.location.search).get('stripe');
  useEffect(() => {
    // retour de l'inscription Stripe : relire l'état du compte
    if (back && status.data?.mode !== 'direct')
      void m.run('POST', '/v1/admin/stripe/refresh', {}).then(
        () => (status.reload(), onChanged()),
        () => undefined,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [back]);
  const connect = async () => {
    const r = (await m.run('POST', '/v1/admin/stripe/onboarding', {})) as { url: string };
    window.location.assign(r.url);
  };
  const s = status.data;
  return (
    <div className="card">
      <h2>Paiement par carte (Stripe)</h2>
      <ErrorBox message={status.error ?? m.error} />
      {s?.mode === 'direct' ? (
        // Stripe Direct : reconnu automatiquement, aucun choix ni compte à relier
        s.chargesEnabled ? (
          <p>
            ✅ Paiements par carte encaissés directement sur le compte Stripe de l’entreprise. Les moyens de
            paiement proposés au client sont ceux activés sur ce compte Stripe.
          </p>
        ) : (
          <p className="muted">
            Paiement par carte direct sur le compte Stripe de l’entreprise : configuration en cours sur le
            serveur.
          </p>
        )
      ) : (
        <StripeConnectSteps
          s={s}
          connect={connect}
          refresh={() =>
            void m.run('POST', '/v1/admin/stripe/refresh', {}).then(
              () => (status.reload(), onChanged()),
              () => undefined,
            )
          }
        />
      )}
    </div>
  );
}

/** Parcours Stripe Connect (inchangé) : relier son compte, terminer l'inscription, prêt. */
function StripeConnectSteps({
  s,
  connect,
  refresh,
}: {
  s: StripeStatus | null;
  connect(): Promise<void>;
  refresh(): void;
}) {
  return (
    <>
      {s && !s.serverReady && (
        <p className="muted">Le paiement par carte n’est pas encore disponible sur ce serveur.</p>
      )}
      {s?.serverReady && !s.connected && (
        <>
          <p>
            Reliez le compte Stripe de votre entreprise : les paiements par carte de vos clients arriveront
            directement sur ce compte, puis sur votre compte bancaire. Aucune commission n’est prélevée par
            l’application (seuls les frais Stripe s’appliquent).
          </p>
          <button className="btn primary" onClick={() => void connect().catch(() => undefined)}>
            Relier mon compte Stripe
          </button>
        </>
      )}
      {s?.connected && !s.chargesEnabled && (
        <>
          <p>⏳ Compte Stripe relié, inscription à terminer chez Stripe (identité, coordonnées bancaires).</p>
          <button className="btn primary" onClick={() => void connect().catch(() => undefined)}>
            Terminer l’inscription Stripe
          </button>{' '}
          <button className="btn" onClick={refresh}>
            Vérifier à nouveau
          </button>
        </>
      )}
      {s?.chargesEnabled && (
        <p>
          ✅ Compte Stripe relié : vos commerciaux peuvent encaisser par carte et envoyer des liens de
          paiement.
        </p>
      )}
    </>
  );
}

/**
 * Moyens de paiement (§10) : ajoutables, renommables, activables / désactivables, réordonnables.
 * Un moyen n'est jamais supprimé : désactivé, il reste sur les paiements passés (libellé figé).
 */
export function PaymentMethodsAdmin() {
  const list = useApi<PaymentMethodRecord[]>('/v1/admin/payment-methods');
  const m = useMutation();
  const [editing, setEditing] = useState<Partial<PaymentMethodRecord> | null>(null);
  const rows = [...(list.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const patch = (id: string, body: Record<string, unknown>) =>
    m.run('PATCH', `/v1/admin/payment-methods/${id}`, body).then(list.reload, () => undefined);
  const move = async (i: number, dir: -1 | 1) => {
    const a = rows[i]!;
    const b = rows[i + dir];
    if (!b) return;
    await m.run('PATCH', `/v1/admin/payment-methods/${a.id}`, { sortOrder: b.sortOrder });
    await m.run('PATCH', `/v1/admin/payment-methods/${b.id}`, {
      sortOrder: a.sortOrder === b.sortOrder ? a.sortOrder + dir : a.sortOrder,
    });
    list.reload();
  };
  return (
    <div>
      <ErrorBox message={list.error ?? m.error} />
      <StripeConnectCard onChanged={list.reload} />
      <div className="card">
        <div className="card-head">
          <h2>Moyens de paiement</h2>
          <button
            className="btn small primary"
            onClick={() =>
              setEditing({
                kind: 'other',
                isActive: true,
                requiresRemittance: true,
                requiresReference: false,
              })
            }
          >
            + Moyen de paiement
          </button>
        </div>
        <p className="muted">
          Proposés à l’encaissement dans cet ordre. « À remettre » : espèces, chèques… restent en attente
          jusqu’à la confirmation de la remise par une personne autorisée.
        </p>
        <ul className="plain">
          {rows.map((r, i) => (
            <li key={r.id} className={`row legal-row${r.isActive ? '' : ' disabled'}`}>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={r.isActive}
                  onChange={() => void patch(r.id, { isActive: !r.isActive })}
                  aria-label={`${r.isActive ? 'Désactiver' : 'Activer'} « ${r.label} »`}
                />
                <span>{r.isActive ? 'Activé' : 'Désactivé'}</span>
              </label>
              <div className="row-main">
                <strong>{r.label}</strong>
                <span className="muted">
                  {PAYMENT_METHOD_KIND_LABELS[r.kind]}
                  {r.requiresRemittance ? ' · à remettre' : ''}
                  {r.requiresReference ? ' · référence obligatoire' : ''}
                  {r.kind === 'stripe' && !r.configured ? ' · ⚠ compte Stripe à relier (ci-dessus)' : ''}
                </span>
              </div>
              <button
                className="btn small"
                aria-label="Monter"
                disabled={i === 0}
                onClick={() => void move(i, -1)}
              >
                ↑
              </button>
              <button
                className="btn small"
                aria-label="Descendre"
                disabled={i === rows.length - 1}
                onClick={() => void move(i, 1)}
              >
                ↓
              </button>
              <button className="btn small" onClick={() => setEditing(r)}>
                Modifier
              </button>
            </li>
          ))}
        </ul>
      </div>
      {editing && (
        <MethodDialog
          value={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function MethodDialog({
  value,
  onClose,
  onSaved,
}: {
  value: Partial<PaymentMethodRecord>;
  onClose(): void;
  onSaved(): void;
}) {
  const m = useMutation();
  const [v, setV] = useState(value);
  const set = (k: keyof PaymentMethodRecord, x: unknown) => setV({ ...v, [k]: x });
  const save = () => {
    const body = {
      label: v.label,
      isActive: v.isActive,
      requiresRemittance: v.requiresRemittance,
      requiresReference: v.requiresReference,
      instructions: v.instructions ?? null,
    };
    const p = v.id
      ? m.run('PATCH', `/v1/admin/payment-methods/${v.id}`, body)
      : m.run('POST', '/v1/admin/payment-methods', { ...body, kind: v.kind });
    p.then(onSaved, () => undefined);
  };
  return (
    <Modal title={v.id ? 'Modifier le moyen de paiement' : 'Nouveau moyen de paiement'} onClose={onClose}>
      <div className="form">
        <Field label="Libellé">
          <Input value={v.label ?? ''} onChange={(e) => set('label', e.target.value)} />
        </Field>
        {!v.id && (
          <Field label="Type">
            <Select value={v.kind} onChange={(e) => set('kind', e.target.value as PaymentMethodKind)}>
              {PAYMENT_METHOD_KINDS.map((k) => (
                <option key={k} value={k}>
                  {PAYMENT_METHOD_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {v.kind !== 'stripe' && (
          <label className="check">
            <input
              type="checkbox"
              checked={!!v.requiresRemittance}
              onChange={(e) => set('requiresRemittance', e.target.checked)}
            />
            À remettre (confirmation de la remise par un responsable)
          </label>
        )}
        <label className="check">
          <input
            type="checkbox"
            checked={!!v.requiresReference}
            onChange={(e) => set('requiresReference', e.target.checked)}
          />
          Référence obligatoire (n° de chèque…)
        </label>
        <Field label="Consignes à l’encaissement (facultatif)">
          <Textarea
            value={v.instructions ?? ''}
            onChange={(e) => set('instructions', e.target.value)}
            placeholder="ex. Chèque à l’ordre de P.H SERVICES"
          />
        </Field>
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" disabled={!v.label?.trim()} onClick={save}>
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}
